import { createContext, useContext } from 'react';
import { dictionaries } from './locales';
import type { Language } from './locales';

export const ApplicationContext = createContext<{
  language: Language;
  readOnly: boolean;
  setLanguage?: (language: Language) => void;
}>({ language: 'en', readOnly: false });

export type Noun =
  | 'post'
  | 'postIn'
  | 'postWith'
  | 'channel'
  | 'channelIn'
  | 'idea'
  | 'avail'
  | 'availability'
  | 'item'
  | 'workspace';

export function createTranslator(language: Language) {
  return (text: string, variables?: Record<string, string | number>) => {
    const localized = dictionaries[language][text];
    if (localized === undefined && import.meta.env.DEV)
      console.warn('Missing translation', language, text);
    let translatedText = localized ?? dictionaries.en[text] ?? text;
    if (variables)
      Object.entries(variables).forEach(([placeholderName, value]) => {
        translatedText = translatedText.split(`{${placeholderName}}`).join(String(value));
      });
    return translatedText;
  };
}

export function createCounter(language: Language) {
  const pluralRules = new Intl.PluralRules(language);
  return (count: number, noun: Noun) => {
    const category = pluralRules.select(count);
    const dictionary = dictionaries[language];
    const form =
      dictionary[`${noun}.${category}`] ??
      dictionaries.en[`${noun}.${category}`] ??
      dictionary[`${noun}.other`] ??
      dictionaries.en[`${noun}.other`] ??
      noun;
    return `${count} ${form}`;
  };
}

export function useApp() {
  const { language, readOnly, setLanguage } = useContext(ApplicationContext);
  const translate = createTranslator(language);
  const formatCount = createCounter(language);
  const readOnlyProps: { disabled?: boolean; title?: string } = readOnly
    ? { disabled: true, title: translate('Read-only view') }
    : {};
  return { language, readOnly, translate, formatCount, readOnlyProps, setLanguage };
}

const DEMO_YEAR = 2026;
const DEMO_MONTH_INDEX_NOVEMBER = 10;

function formatDemoNovemberDay(dayOfMonth: string, language: string) {
  return new Intl.DateTimeFormat(language, { day: 'numeric', month: 'short' }).format(
    new Date(DEMO_YEAR, DEMO_MONTH_INDEX_NOVEMBER, Number(dayOfMonth))
  );
}

export function timeLabel(at: string, language: string, translate: (text: string) => string) {
  const novemberDayMatch = /^(\d{1,2}) Nov$/.exec(at);
  if (novemberDayMatch) return formatDemoNovemberDay(novemberDayMatch[1], language);
  const isClockTimeOrDate = /\d/.test(at);
  return isClockTimeOrDate ? at : translate(at);
}

export function stampLabel(at: string, language: string) {
  return at.replace(/^(\d{1,2}) Nov/, (_, dayOfMonth: string) =>
    formatDemoNovemberDay(dayOfMonth, language)
  );
}
